const SCROLL_PER_TAB_VH = 0.5;
const TRACK_SELECTOR = ".js-benefits-switcher-track";
const SPACER_SELECTOR = ".js-benefits-switcher-spacer";
// Small pixel tolerance to avoid boundary flicker from sub-pixel rounding.
const EPS = 2;

function isAttached(...elements) {
  return elements.every($el => $el && document.contains($el));
}

function clamp01(x) {
  const n = Number(x);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function tabFromProgress(progress, count) {
  const p = clamp01(progress);
  if (p <= 0) return 0;
  if (p >= 1) return count - 1;
  return Math.min(Math.floor(p * count), count - 1);
}

function getProgress(trackEl, sectionEl, scrollDistance) {
  if (scrollDistance <= 0) return 0;
  const sectionRect = sectionEl.getBoundingClientRect();
  const trackTop = trackEl.getBoundingClientRect().top;
  const viewportBottom = window.innerHeight;
  const stuckAtTop = sectionRect.top <= 0;
  const stuckAtBottom = sectionRect.bottom <= viewportBottom && trackTop <= 0;
  if (!stuckAtTop && !stuckAtBottom) return 0;
  if (trackTop > EPS) return 0;
  if (trackTop <= -scrollDistance + EPS) return 1;
  return clamp01(-trackTop / scrollDistance);
}

function recalcSizes(ctx) {
  if (!isAttached(ctx.sectionEl, ctx.trackEl)) return;
  const vh = Math.max(0, window.innerHeight);
  ctx.scrollDistance = Math.max(1, ctx.count * vh * SCROLL_PER_TAB_VH);
  const sectionHeight = Math.max(0, ctx.sectionEl.offsetHeight);
  ctx.trackEl.style.height = `${sectionHeight + ctx.scrollDistance}px`;
  const spacerEl = ctx.trackEl.querySelector(SPACER_SELECTOR);
  if (spacerEl) spacerEl.style.height = `${ctx.scrollDistance}px`;
}

function ensureScrollDistance(ctx) {
  if (ctx.scrollDistance > 0) return;
  recalcSizes(ctx);
}

function onScroll(ctx) {
  if (ctx.raf !== null) return;
  ctx.raf = window.requestAnimationFrame(() => {
    ctx.raf = null;
    if (!isAttached(ctx.sectionEl, ctx.trackEl)) return;
    ensureScrollDistance(ctx);
    if (ctx.scrollDistance <= 0) return;
    const progress = getProgress(ctx.trackEl, ctx.sectionEl, ctx.scrollDistance);
    const idx = tabFromProgress(progress, ctx.count);
    if (idx !== ctx.getIdx()) ctx.onChange(ctx.names[idx]);
  });
}

function createContext({ sectionEl, trackEl, tabNames, getActiveIndex, onSlideChange }) {
  return {
    sectionEl,
    trackEl,
    names: tabNames,
    count: tabNames.length,
    getIdx: getActiveIndex,
    onChange: onSlideChange,
    scrollDistance: 0,
    raf: null,
    destroyed: false
  };
}

export function scrollToTab(ctx, name) {
  if (ctx.destroyed || !isAttached(ctx.sectionEl, ctx.trackEl)) return;
  const idx = ctx.names.indexOf(name);
  if (idx < 0) return;
  ensureScrollDistance(ctx);
  if (ctx.scrollDistance <= 0) return;

  const trackTop = ctx.trackEl.getBoundingClientRect().top;
  const sectionRect = ctx.sectionEl.getBoundingClientRect();
  const viewportBottom = window.innerHeight;
  const stuckAtTop = sectionRect.top <= 0;
  const stuckAtBottom = sectionRect.bottom <= viewportBottom;
  const isStuck = (stuckAtTop || stuckAtBottom) && trackTop <= 0;
  if (!isStuck) return;

  const trackTopDocument = ctx.trackEl.getBoundingClientRect().top + window.scrollY;
  const tabMid = (idx + 0.5) / ctx.count;
  const targetY = trackTopDocument + tabMid * ctx.scrollDistance;
  const maxScrollY = document.documentElement.scrollHeight - window.innerHeight;
  const clampedY = Math.max(0, Math.min(targetY, maxScrollY));
  window.scrollTo({ top: clampedY, behavior: "instant" });
}

export function destroyWheel(ctx, handleScroll, handleResize) {
  if (ctx.destroyed) return;
  ctx.destroyed = true;
  if (ctx.raf !== null) window.cancelAnimationFrame(ctx.raf);
  ctx.raf = null;
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", handleResize);
}

export default function initBenefitsSwitcherWheel({
  sectionEl,
  tabNames,
  getActiveIndex,
  onSlideChange
}) {
  if (tabNames.length === 0) return null;

  const trackEl = sectionEl.closest(TRACK_SELECTOR);
  if (!trackEl) return null;

  const ctx = createContext({
    sectionEl,
    trackEl,
    tabNames,
    getActiveIndex,
    onSlideChange
  });
  const handleScroll = () => onScroll(ctx);
  const handleResize = () => recalcSizes(ctx);

  recalcSizes(ctx);
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", handleResize);

  return {
    scrollToTab: name => scrollToTab(ctx, name),
    destroy: () => destroyWheel(ctx, handleScroll, handleResize)
  };
}
